import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, ChevronDown, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Hero() {
  const [patientType, setPatientType] = useState<'new' | 'existing'>('new');
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const cardImages = [{
    src: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&q=80&w=600',
    alt: 'Happy Patient'
  }, {
    src: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&q=80&w=600',
    alt: 'Dental Clinic Room'
  }, {
    src: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&q=80&w=600',
    alt: 'Dental Tools'
  }];
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCardIndex(prev => (prev + 1) % cardImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const visitReasons = ['Braces / Retainer Checkup', 'Braces Consultation', 'Clear Aligners / Invisalign Consultation', 'Install Braces', 'Retainer Installation', 'Braces / Clear Aligners / Retainer Removal', 'Braces / Clear Aligners Repair or Replacement', 'Gapped Teeth', 'Install Clear Aligners / Invisalign', 'Lingual Bar Retainers', 'Retainer Checkup'];
  const insurances = ['Aetna', 'BlueCross BlueShield', 'Cigna', 'Delta Dental', 'Guardian', 'Metlife', 'DC Medicaid'];
  return <section id="about" className="relative pt-48 md:pt-40 pb-24 px-4 overflow-hidden" data-id="element-224">
      {/* Video Background */}
      <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-225">
        <source src="https://cdn.magicpatterns.com/uploads/eN5ftQz7jXFdUwThVwRk5R/6529141-hd_2048_1080_25fps.mp4" type="video/mp4" data-id="element-226" />
      </video>

      {/* Dark Overlay for Text Readability */}
      <div className="absolute inset-0 bg-[#225274]/80 mix-blend-multiply" data-id="element-227" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#225274]/90 via-[#225274]/60 to-[#225274]/90" data-id="element-228" />

      <div className="relative z-10 max-w-7xl mx-auto px-4" data-id="element-229">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-230">
          {/* Left Column: Text + Stacked Images */}
          <div className="flex flex-col gap-12" data-id="element-231">
            <div className="text-left space-y-6" data-id="element-232">
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-3xl md:text-5xl font-extrabold text-white leading-tight" data-id="element-233">
                DENTAL CARE,
                <br data-id="element-234" />
                BUILT AROUND YOU.
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="text-md md:text-lg text-white/90 max-w-xl" data-id="element-235">
                Accepting All Patients. We proudly cater to Medicaid and Public
                Aid as well as Private Insurance providers.
              </motion.p>
            </div>

            {/* Card A: Stacked Clinic Images with Auto-Rotation */}
            <div className="relative h-[450px] w-full flex items-center justify-center group perspective-1000" data-id="element-236">
              <AnimatePresence mode="wait" data-id="element-237">
                <motion.div key={activeCardIndex} initial={{
                opacity: 0,
                scale: 0.8,
                x: -50,
                rotate: -10
              }} animate={{
                opacity: 1,
                scale: 1,
                x: 0,
                rotate: 0
              }} exit={{
                opacity: 0,
                scale: 1.1,
                x: 50,
                rotate: 10
              }} transition={{
                duration: 0.8,
                ease: 'circOut'
              }} className="absolute w-72 h-96 rounded-[12px] overflow-hidden shadow-3xl border-4 border-white z-30" data-id="element-238">
                  <img src={cardImages[activeCardIndex].src} alt={cardImages[activeCardIndex].alt} className="w-full h-full object-cover" data-id="element-239" />
                  {/* Overlay Badge - Only for Happy Patient */}
                  {activeCardIndex === 0 && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#225274] to-transparent p-6 pt-12" data-id="element-240">
                      <div className="flex items-center gap-2 mb-2" data-id="element-241">
                        <ShieldAlert className="text-[#54c595]" size={20} data-id="element-242" />
                        <span className="text-white font-bold text-sm tracking-wider uppercase" data-id="element-243">
                          Medicaid Accepted
                        </span>
                      </div>
                      <p className="text-white/90 text-sm leading-tight" data-id="element-244">
                        We believe everyone deserves a healthy smile with no
                        hidden fees.
                      </p>
                    </div>}
                </motion.div>
              </AnimatePresence>

              {/* Decorative Background Cards (Static) */}
              <div className="absolute w-64 h-80 rounded-[12px] overflow-hidden shadow-2xl border-4 border-white transform -rotate-12 -translate-x-12 z-10 opacity-40" data-id="element-245">
                <img src={cardImages[(activeCardIndex + 1) % 3].src} className="w-full h-full object-cover grayscale" alt="" data-id="element-246" />
              </div>
              <div className="absolute w-64 h-80 rounded-[12px] overflow-hidden shadow-2xl border-4 border-white transform rotate-12 translate-x-12 z-20 opacity-60" data-id="element-247">
                <img src={cardImages[(activeCardIndex + 2) % 3].src} className="w-full h-full object-cover grayscale" alt="" data-id="element-248" />
              </div>
            </div>
          </div>

          {/* Card B: Booking Widget */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-[#225274] border-2 border-white/10 rounded-[12px] p-8 shadow-2xl hover:shadow-3xl transition-shadow text-left text-white flex flex-col justify-between backdrop-blur-sm" data-id="element-249">
            <div data-id="element-250">
              <div className="flex items-center gap-3 mb-6" data-id="element-251">
                <div className="w-12 h-12 bg-[#54c595]/20 rounded-full flex items-center justify-center" data-id="element-252">
                  <Calendar className="text-[#54c595]" size={24} data-id="element-253" />
                </div>
                <h3 className="text-2xl font-bold" data-id="element-254">Book an appointment</h3>
              </div>

              {/* Patient Type Toggle */}
              <div className="flex bg-white/10 rounded-full p-1 mb-6" data-id="element-255">
                <button onClick={() => setPatientType('new')} className={`flex-1 py-2 text-xs font-bold rounded-full transition-all uppercase tracking-wider ${patientType === 'new' ? 'bg-gradient-to-r from-[#225274] via-[#4bafbc] to-[#54c595] text-white shadow-sm' : 'text-white/70 hover:text-white'}`} data-id="element-256">
                  New patient
                </button>
                <button onClick={() => setPatientType('existing')} className={`flex-1 py-2 text-xs font-bold rounded-full transition-all uppercase tracking-wider ${patientType === 'existing' ? 'bg-gradient-to-r from-[#225274] via-[#4bafbc] to-[#54c595] text-white shadow-sm' : 'text-white/70 hover:text-white'}`} data-id="element-257">
                  Existing patient
                </button>
              </div>

              <div className="space-y-4 mb-8" data-id="element-258">
                {/* Reason Dropdown */}
                <div className="relative" data-id="element-259">
                  <label className="block text-xs font-bold text-white/70 mb-1 uppercase tracking-wider" data-id="element-260">
                    Reason for visit
                  </label>
                  <select className="w-full bg-white/5 border border-white/20 rounded-lg py-3 px-4 appearance-none text-white focus:outline-none focus:ring-2 focus:ring-[#54c595] focus:border-transparent transition-all" data-id="element-261">
                    <option value="" disabled selected className="text-slate-800" data-id="element-262">
                      Select reason...
                    </option>
                    {visitReasons.map((reason, idx) => <option key={idx} value={reason} className="text-slate-800" data-id="element-263">
                        {reason}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-4 top-[34px] text-white/50 pointer-events-none" size={18} data-id="element-264" />
                </div>

                {/* Insurance Dropdown */}
                <div className="relative" data-id="element-265">
                  <label className="block text-xs font-bold text-white/70 mb-1 uppercase tracking-wider" data-id="element-266">
                    Insurance carrier and plan
                  </label>
                  <select className="w-full bg-white/5 border border-white/20 rounded-lg py-3 px-4 appearance-none text-white focus:outline-none focus:ring-2 focus:ring-[#54c595] focus:border-transparent transition-all" data-id="element-267">
                    <option value="" disabled selected className="text-slate-800" data-id="element-268">
                      Select insurance...
                    </option>
                    {insurances.map((ins, idx) => <option key={idx} value={ins} className="text-slate-800" data-id="element-269">
                        {ins}
                      </option>)}
                    <option value="other" className="text-slate-800" data-id="element-270">
                      Other / Self-Pay
                    </option>
                  </select>
                  <ChevronDown className="absolute right-4 top-[34px] text-white/50 pointer-events-none" size={18} data-id="element-271" />
                </div>
              </div>
            </div>

            <button onClick={() => window.location.href = '/book'} className="w-full bg-gradient-to-r from-[#225274] via-[#4bafbc] to-[#54c595] hover:brightness-110 text-white font-bold py-4 rounded-full transition-all shadow-lg shadow-teal-900/20 flex items-center justify-center gap-2 uppercase tracking-widest" data-id="element-272">
              <Calendar size={18} data-id="element-273" />
              FIND AVAILABLE TIMES
            </button>
          </motion.div>
        </div>
      </div>
    </section>;
}